import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Mail, Lock, User, Building, ArrowRight, Eye, EyeOff, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const MAUD_IMAGE = "https://customer-assets.emergentagent.com/job_vivant-responsible/artifacts/qjuiwm85_%40PARLACOM-MAUD-06.jpg";

const Auth = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company: ""
  });

  if (isAuthenticated) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Bienvenue dans votre écosystème !");
      } else {
        if (!formData.name) {
          toast.error("Veuillez entrer votre nom");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name, formData.company);
        toast.success("Votre écosystème est prêt !");
      }
      navigate("/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[var(--bone)]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div>
              <span className="font-display text-2xl font-bold tracking-tight text-[var(--charcoal)]">
                ALMA <span className="text-[var(--terracotta)]">VIVU</span>
              </span>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--stone)]">L'âme vivante</p>
            </div>
          </Link>

          {/* Title */}
          <div className="mb-10">
            <h1 className="font-display text-4xl font-bold text-[var(--charcoal)] mb-3">
              {isLogin ? "Bon retour" : "Rejoignez-nous"}
            </h1>
            <p className="text-[var(--stone)]">
              {isLogin 
                ? "Accédez à votre écosystème et poursuivez votre transformation."
                : "Créez votre espace pour suivre votre progression et accéder aux formations."
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <label htmlFor="name" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                    Nom complet
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Votre nom"
                      className="input-premium pl-12"
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="company" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                    Entreprise (optionnel)
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Votre entreprise"
                      className="input-premium pl-12"
                      data-testid="company-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <label htmlFor="email" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.com"
                  className="input-premium pl-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="input-premium pl-12 pr-12"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--stone)] hover:text-[var(--terracotta)] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-primary w-full flex items-center justify-center gap-2 mt-8"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-[var(--bone)] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {isLogin ? "Accéder à mon écosystème" : "Créer mon écosystème"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Toggle */}
          <div className="mt-10 text-center">
            <p className="text-[var(--stone)]">
              {isLogin ? "Pas encore d'écosystème ?" : "Déjà membre ?"}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-[var(--terracotta)] font-semibold hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isLogin ? "Créer un compte" : "Se connecter"}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-[var(--maquis)] relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[var(--terracotta)] opacity-20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[var(--bone)] opacity-10 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 text-[var(--bone)]">
          <div className="space-y-8">
            {/* Maud Image */}
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[var(--bone)]/20">
              <img 
                src={MAUD_IMAGE} 
                alt="Maud"
                className="w-full h-full object-cover"
              />
            </div>

            <h2 className="font-display text-4xl font-bold leading-tight">
              Votre parcours vers une entreprise{" "}
              <span className="text-[var(--terracotta-light)] emphasis">régénérative</span>
            </h2>
            <p className="text-[var(--bone)]/80 text-lg leading-relaxed">
              En créant votre écosystème, vous accédez à des formations, 
              un suivi personnalisé et une communauté engagée pour la transition.
            </p>

            <div className="space-y-4 pt-4">
              {[
                "Formations vidéo et quiz de validation",
                "Accompagnement personnalisé avec Maud",
                "Parcours certifiants et badges",
                "Podcast exclusif Maquis Vivant"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 bg-[var(--terracotta)] rounded-full flex items-center justify-center">
                    <Sparkles className="w-3 h-3 text-[var(--bone)]" />
                  </div>
                  <span className="text-[var(--bone)]/90">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
